<script>
import {computed, onBeforeMount, ref, watch} from "vue";
import {Link} from "@inertiajs/vue3";

import ProductDeliveryInfo from "@js/components/Sections/Product/ProductDeliveryInfo.vue";
import ProductSelfPickupInfo from "@js/components/Sections/Product/ProductSelfPickupInfo.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import IconCreditCard from "@js/components/Icons/IconCreditCard.vue";
import ProductCartForm from "@js/components/Sections/Product/ProductCartForm.vue";
import FastBuyCallback from "@js/components/Callbacks/FastBuyCallback.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import ProductCharacteristicsFeature from "@js/components/Sections/Product/ProductCharacteristicsFeature.vue";
import TabsComponent from "@js/components/Tabs/TabsComponent.vue";
import ProductAllCharacteristics from "@js/components/Sections/Product/Components/ProductAllCharacteristics.vue";
import ProductFaq from "@js/components/Sections/Product/Components/ProductFaq.vue";
import TextAccordion from "@js/components/Sections/TextBlocks/TextAccordion.vue";
import {useCartStore} from "@js/store/CartStore.js";
import {ecomDetail, mapProduct} from "@js/ecommerce.js";
import IconDeliveryCar from "@js/components/Icons/IconDeliveryCar.vue";
import Tooltip from "@js/components/Tooltip.vue";
import IconServiceSawing from "@js/components/Icons/IconServiceSawing.vue";
import IconService1Point5TonDelivery from "@js/components/Icons/IconService1Point5TonDelivery.vue";
import IconSeviceNoPassengerLift from "@js/components/Icons/IconSeviceNoPassengerLift.vue";
import IconServiceNoLiftLifting from "@js/components/Icons/IconServiceNoLiftLifting.vue";
import IconService5TonDelivery from "@js/components/Icons/IconService5TonDelivery.vue";
import IconServiceNoLift from "@js/components/Icons/IconServiceNoLift.vue";
import IconServiceUnloadingOnly from "@js/components/Icons/IconServiceUnloadingOnly.vue";
import BaseImageSlider from "@js/components/Images/BaseImageSlider.vue";
import IconImage from "@js/components/Icons/IconImage.vue";

export default {
    name: "ProductPage",
    props: {
        product: {
            required: true,
            type: Object,
        },
        characteristics_grouped: {
            required: false,
            type: [Array, Object],
            default: () => [],
        },
    },
    data() {
        return {
            tabs: [
                {id: 1, title: "Описание"},
                {id: 2, title: "Все характеристики"},
                {id: 3, title: "Частые вопросы"},
            ],
        };
    },
    components: {
        IconImage,
        BaseImageSlider,
        TextAccordion,
        ProductFaq,
        ProductAllCharacteristics,
        TabsComponent,
        ProductCharacteristicsFeature,
        Breadcrumbs,
        FastBuyCallback,
        ProductCartForm,
        IconCreditCard,
        Link,
        OnlyDesktop,
        OnlyMobile,
        MobileTopBar,
        IconDeliveryCar,
        ProductSelfPickupInfo,
        ProductDeliveryInfo,
        Tooltip,
    },
    computed: {
        imagesList() {
            const images = this.product?.media?.images || this.product?.images || [];

            if (!images.length) {
                return ['/assets/defaults/product-not-found.webp'];
            }

            return images.map((image) => "/storage/products/" + image);
        },
    },
    setup(props) {
        const allCharsRef = ref(null);
        const currentTab = ref(1);

        const productStore = useProductStore();
        const cartStore = useCartStore();

        const serviceIcons = {
            isSawing: IconServiceSawing,
            isMinVehicle15: IconService1Point5TonDelivery,
            isNoPassengerLift: IconSeviceNoPassengerLift,
            isNoLiftLifting: IconServiceNoLiftLifting,
            isMinVehicle5: IconService5TonDelivery,
            isNoLifting: IconServiceNoLift,
            isUnloadingOnly: IconServiceUnloadingOnly,
        };

        const syncProduct = (product) => {
            if (!product) return;

            productStore.product = product;
            productStore.characteristics_grouped = props.characteristics_grouped;

            cartStore.hydrate?.();
            cartStore.registerCatalogProduct(product);
        };

        syncProduct(props.product);

        watch(
            () => props.product,
            (product) => {
                syncProduct(product);
            },
            {deep: true}
        );

        watch(
            () => props.characteristics_grouped,
            (value) => {
                productStore.characteristics_grouped = value;
            },
            {deep: true}
        );

        const normalizedCharacteristics = computed(() => {
            return Array.isArray(props.characteristics_grouped)
                ? props.characteristics_grouped
                : Object.values(props.characteristics_grouped || {});
        });

        const base_chars = computed(() => normalizedCharacteristics.value.slice(0, 5));

        const productServices = computed(() => {
            return Array.isArray(props.product?.services) ? props.product.services : [];
        });

        const servicesTitle = computed(() => {
            return productServices.value?.[0]?.type?.title || "Условия доставки и возможные услуги";
        });

        const hasServices = computed(() => {
            return productServices.value.length > 0;
        });

        const getServiceIcon = (serviceValue) => {
            if (!serviceValue) return null;
            return serviceIcons[serviceValue] || null;
        };

        onBeforeMount(() => {
            ecomDetail(mapProduct(props.product, {list: "PDP", position: 1}));
        });

        return {
            productStore,
            cartStore,
            base_chars,
            currentTab,
            allCharsRef,
            productServices,
            servicesTitle,
            hasServices,
            getServiceIcon,
        };
    },
};
</script>

<template>
    <MobileTopBar/>

    <div class="container">
        <Breadcrumbs/>

        <OnlyMobile :maxSize="1024">
            <h1 class="flex items-center text-2xl md:text-3xl font-bold mb-3">
                {{ product.title }}
            </h1>
        </OnlyMobile>

        <!-- Верхняя часть -->
        <section class="grid grid-cols-12 gap-5 mb-8 lg:mb-12">
            <!-- Фото -->
            <div class="col-span-12 md:col-span-7 lg:col-span-4 order-1">
                <div class="w-full max-w-[560px] mx-auto lg:mx-0">
                    <template v-if="imagesList.length">
                        <BaseImageSlider
                            :images="imagesList"
                            thumbs-direction="vertical"
                            slider-height-class="aspect-square"
                        />
                    </template>

                    <template v-else>
                        <div
                            class="aspect-square w-full rounded-2xl border border-gray-200 bg-gray-100 flex items-center justify-center"
                        >
                            <IconImage class="w-20 h-20 text-gray-400"/>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Контент -->
            <div class="col-span-12 lg:col-span-5 order-3 lg:order-2 flex flex-col gap-5">
                <OnlyDesktop :maxSize="1024">
                    <h1 class="flex items-center text-2xl md:text-3xl font-bold mb-0">
                        {{ product.title }}
                    </h1>
                </OnlyDesktop>

                <div class="rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
                    <div class="text-lg font-semibold text-gray-950 mb-4">
                        Характеристики
                    </div>

                    <div class="flex flex-col gap-2.5">
                        <ProductCharacteristicsFeature
                            v-for="char in base_chars"
                            :key="char.type_id || char.title"
                            :title="char.title"
                            :description="char.values"
                        />
                    </div>
                </div>

                <div
                    v-if="hasServices"
                    class="rounded-2xl border border-gray-200 bg-white p-4 md:p-5"
                >
                    <div class="text-sm italic text-gray-700 mb-3">
                        {{ servicesTitle }}:
                    </div>

                    <div class="flex flex-wrap gap-2.5">
                        <Tooltip
                            v-for="service in productServices"
                            :key="service.id"
                            :text="service.description || service.title"
                            placement="top"
                            :offset="10"
                        >
                            <button
                                type="button"
                                class="group relative flex items-center justify-center w-12 h-12 rounded-xl border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:-translate-y-0.5 hover:border-gray-500 hover:shadow-md active:scale-[0.98]"
                                :aria-label="service.title"
                            >
                                <component
                                    :is="getServiceIcon(service.value)"
                                    v-if="getServiceIcon(service.value)"
                                    class="w-6 h-6 shrink-0 text-gray-700 group-hover:text-black transition"
                                />

                                <div
                                    v-else
                                    class="flex items-center justify-center w-6 h-6 rounded-md border border-gray-400 text-[10px] font-semibold text-gray-500"
                                >
                                    ?
                                </div>

                                <span
                                    class="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gray-400 opacity-0 group-hover:opacity-100 transition"
                                ></span>
                            </button>
                        </Tooltip>
                    </div>
                </div>

                <div
                    v-else
                    class="grid grid-cols-1 md:grid-cols-2 gap-3"
                >
                    <Link
                        :href="route('aboutPayment')"
                        class="flex items-center justify-start gap-3 text-sm w-full border border-dashed border-gray-400 text-black p-4 rounded-2xl md:hover:bg-baseColor/10 font-semibold transition"
                    >
                        <IconCreditCard class="min-w-7 max-w-7 min-h-7 max-h-7"/>
                        <span>Оплата наличными и безналичным</span>
                    </Link>

                    <Link
                        :href="route('deliveryAndShipping')"
                        class="flex items-center justify-start gap-3 text-sm w-full border border-dashed border-gray-400 text-black p-4 rounded-2xl md:hover:bg-baseColor/10 font-semibold transition"
                    >
                        <IconDeliveryCar class="min-w-7 max-w-7 min-h-7 max-h-7"/>
                        <span>Доставка по МСК и МО от 1 дня</span>
                    </Link>
                </div>
            </div>

            <!-- Карточка покупки -->
            <div class="col-span-12 md:col-span-5 lg:col-span-3 order-2 lg:order-3">
                <div class="lg:sticky lg:top-24">
                    <ProductCartForm
                        v-if="productStore.product"
                        :key="product.id"
                        :product-id="product.id"
                    />
                </div>
            </div>
        </section>

        <!-- Нижняя часть -->
        <section class="grid grid-cols-12 gap-y-8 lg:gap-x-8 xl:gap-x-10 text-gray-900">
            <div class="col-span-12 lg:col-span-8" ref="allCharsRef">
                <div class="rounded-2xl mb-10">
                    <TabsComponent v-model="currentTab" :tabs="tabs"/>

                    <div class="border border-gray-200 rounded-2xl p-5 md:p-6 bg-white">
                        <div v-show="currentTab === 1">
                            <TextAccordion :minHeight="285" v-if="product?.media?.description">
                                <div
                                    class="flex flex-col gap-1 bbp"
                                    v-html="product.media.description"
                                ></div>
                            </TextAccordion>

                            <div v-else class="text-sm text-gray-600 italic">
                                Описания пока нет
                            </div>
                        </div>

                        <div v-show="currentTab === 2">
                            <ProductAllCharacteristics
                                :characteristics_grouped="characteristics_grouped"
                                :category-slug="product?.category?.slug"
                                :type-slug="product?.type?.slug"
                            />
                        </div>

                        <div v-show="currentTab === 3">
                            <ProductFaq :items="product?.media?.faq || []"/>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-span-12 lg:col-span-4">
                <div class="flex flex-col gap-5">
                    <div class="rounded-2xl overflow-hidden">
                        <ProductDeliveryInfo/>
                    </div>

                    <div class="rounded-2xl overflow-hidden">
                        <ProductSelfPickupInfo/>
                    </div>
                </div>
            </div>
        </section>
    </div>

    <FastBuyCallback/>
</template>

<style lang="scss">
.bbp {
    --bbp-text: #000;
    --bbp-head: #000;
    --bbp-muted: #000;
    --bbp-accent: var(--main-color);
    --bbp-border: #000;

    color: var(--bbp-text);
    font-size: 0.95rem;
    line-height: 1.55;
    word-break: normal;
    overflow-wrap: anywhere;

    > *:first-child {
        margin-top: 0;
    }

    > *:last-child {
        margin-bottom: 0;
    }

    p {
        margin: 0;
    }

    h2, h3 {
        color: var(--bbp-head);
        font-weight: 600;
        line-height: 1.25;
    }

    h2, h3 {
        font-size: clamp(1rem, 0.95rem + 0.4vw, 1.2rem);
    }

    h2 + ul,
    h3 + ul,
    h3 + p,
    h2 + p {
        margin-top: 0.5em;
    }

    p + h2,
    p + h3,
    ul + h2,
    ul + h3 {
        margin-top: 1em;
    }

    ul {
        list-style: none;
        padding: 0;

        > li {
            position: relative;
            padding-left: 1.25em;
        }

        > li::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0.6em;
            width: 0.5em;
            height: 0.5em;
            border-radius: 50%;
            background: var(--bbp-accent);
        }
    }

    @media (min-width: 768px) {
        ul > li {
            margin: 0.4em 0;
        }
    }

    @media (prefers-color-scheme: dark) {
        --bbp-text: #000;
        --bbp-head: #000;
        --bbp-muted: #000;
        --bbp-border: #000;
        --bbp-accent: var(--main-color);
    }
}
</style>
